import {
  all,
  call,
  delay,
  put,
  race,
  select,
  take,
  takeLatest,
} from 'redux-saga/effects'
import {
  databaseForRoomsIsEmpty,
  dataForRoomsIsAccepted,
  dataForRoomsIsRejected,
  IDatabaseForRoomsEmpty,
  IDataForRoomsIsAccepted,
} from '../../actions/bookingCalendar/room/requestDataForRooms'
import { callFetch } from '../../actions/fetch'
import { fireToastWarning } from '../../actions/toasts/fireToast'
import {
  isAndroidWebView,
  isEmbeddedInIframe,
  isIOSWebView,
} from '../../components/DetectBrowser/DetectBrowser'
import { database } from '../../database'
import { IUserData, IWithColor } from '../../database/indexedDb'
import i18n from '../../i18n'
import { fetchRooms, IRoom } from '../../models/Room'
import {
  CalendarActions,
  RoomActions,
} from '../../reducers/bookingCalendar/actionNames'
import { getCalendarId } from '../../reducers/bookingCalendar/selectors/calendar'
import { AuthorizationActions } from '../../reducers/userAccount/actionNames'
import { getIsAuthorizing } from '../../reducers/userAccount/selectors/credentials'
import { toColor } from '../../utils/ColorTransformator'
import { ActionNames } from '../../utils/FetchActionsName'
import fetchActionsName from '../../utils/FetchActionsName/fetchActionsName'
import takeLatestWithSignOff from '../../utils/Saga/TakeLatestUntilSignoff'

function* loadRoomsFromDatabase(action) {
  try {
    yield race({
      done: take(CalendarActions.CalendarsFetched),
      fail: delay(1000),
    })

    const calendarId = yield select(getCalendarId)
    if (undefined === calendarId) {
      yield delay(1000)
      yield put(action)
    } else if (isIOSWebView() || isAndroidWebView() || isEmbeddedInIframe()) {
      yield put(databaseForRoomsIsEmpty())
      return
    }

    const roomsInDB: (IRoom & IWithColor)[] = yield database.rooms
      .where('calendarId')
      .equals(calendarId)
      .toArray()

    if (roomsInDB.length > 0) {
      const rooms = roomsInDB.map(room => ({
        ...room,
        customColor: toColor(room.savedColor),
        modified: 0,
      }))

      yield put(dataForRoomsIsAccepted(rooms, false))
      return
    }

    yield put(databaseForRoomsIsEmpty())
  } catch (e) {
    yield fireToastWarning(i18n.t('error_server_response_4'))
  }
}

function* loadRoomsFromApi(action: IDatabaseForRoomsEmpty) {
  try {
    const actionNames = fetchActionsName(ActionNames.FetchRoom)
    const [{ calendarId, service }] = yield all([
      take(actionNames.output),
      put(callFetch(actionNames.input, actionNames.output, fetchRooms)),
    ])

    const rooms = yield call(service, calendarId, action.lastModifyDate)
    yield put(dataForRoomsIsAccepted(rooms.map(r => ({ ...r, modified: 0 }))))
  } catch (e) {
    yield put(
      dataForRoomsIsRejected(
        e.message + `/n l:${action.lastModifyDate} /n` + e.stack,
      ),
    )
  }
}

function* putToIndexedDB(action: IDataForRoomsIsAccepted) {
  if (!action.putToDB) {
    return
  }

  try {
    const isAuthorizing = yield select(getIsAuthorizing)
    if (isAuthorizing) {
      yield delay(500)
    }

    const calendarId = yield select(getCalendarId)
    const rooms = action.rooms.map((room): IRoom & IUserData & IWithColor => ({
      ...room,
      calendarId,
      savedColor: room.customColor.rgb().toString(),
    }))

    yield database.rooms.bulkPut(rooms)
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('loadRooms:putToIndexedDB', e.message)
  }
}

function* clearRedux() {
  yield put(dataForRoomsIsAccepted([], false, true))
}

export default function*() {
  yield takeLatestWithSignOff(
    RoomActions.RequestDataForRooms,
    loadRoomsFromDatabase,
  )
  yield takeLatestWithSignOff(
    RoomActions.DatabaseForRoomsIsEmpty,
    loadRoomsFromApi,
  )
  yield takeLatestWithSignOff(RoomActions.DataForRoomsAccepted, putToIndexedDB)
  yield takeLatest(AuthorizationActions.SignOffTheAccount, clearRedux)
}
